// src/hooks/useGoogleCalendarSync.js
import { useState, useCallback } from 'react';
import { mergeEvents, mapMyloEventToGoogle } from '../utils/googleCalendarSync';
import { fetchAllGoogleEvents, createGoogleEvent, removeGoogleEvent } from '../utils/googleCalendarApi';

const SYNC_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes
const DATE_RANGE_BACK  = 60;
const DATE_RANGE_FWD   = 90;

export function useGoogleCalendarSync({ setSt, events }) {
  const [syncing, setSyncing]       = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncError, setSyncError]   = useState(null);

  const syncFromGoogle = useCallback(async (silent = false) => {
    setSyncing(true);
    setSyncError(null);
    try {
      const start = new Date(); start.setDate(start.getDate() - DATE_RANGE_BACK);
      const end   = new Date(); end.setDate(end.getDate() + DATE_RANGE_FWD);
      const googleEvents = await fetchAllGoogleEvents(start, end);
      setSt(p => ({ ...p, events: mergeEvents(p.events, googleEvents) }));
      const now = new Date();
      setLastSynced(now);
      try { localStorage.setItem('mylo_gcal_last_sync', now.getTime().toString()); } catch {}
      if (!silent) return googleEvents.length;
    } catch (err) {
      setSyncError(err.message);
      if (!silent) throw err;
      else console.warn('[gcal-sync-silent]', err.message);
    } finally {
      setSyncing(false);
    }
    return 0;
  }, [setSt]);

  const autoSyncIfStale = useCallback(() => {
    try {
      // Don't sync if no user ID yet — auth may not have loaded
      const uid = localStorage.getItem('mylo_user_id') || '';
      if (!uid) {
        // Retry after 3s to give auth time to load
        setTimeout(() => {
          const uid2 = localStorage.getItem('mylo_user_id') || '';
          if (uid2) syncFromGoogle(true).catch(() => {});
        }, 3000);
        return;
      }
      const last = parseInt(localStorage.getItem('mylo_gcal_last_sync') || '0');
      if (Date.now() - last > SYNC_COOLDOWN_MS) {
        syncFromGoogle(true).catch(() => {});
      } else {
        const lastDate = last ? new Date(last) : null;
        if (lastDate) setLastSynced(lastDate);
      }
    } catch {}
  }, [syncFromGoogle]);

  const pushEventToGoogle = useCallback(async (myloEvent) => {
    if (myloEvent.source === 'google') return;
    try {
      const googleEvent = mapMyloEventToGoogle(myloEvent);
      const created = await createGoogleEvent(googleEvent);
      if (created.id) {
        setSt(p => ({
          ...p,
          events: p.events.map(e =>
            e.id === myloEvent.id ? { ...e, googleId: created.id, source: 'google' } : e
          ),
        }));
      }
    } catch (err) { console.warn('[gcal-push]', err.message); }
  }, [setSt]);

  const deleteGoogleEvent = useCallback(async (googleId) => {
    if (!googleId) return;
    try { await removeGoogleEvent(googleId); }
    catch (err) { console.warn('[gcal-delete]', err.message); }
  }, []);

  return { syncFromGoogle, autoSyncIfStale, pushEventToGoogle, deleteGoogleEvent, syncing, lastSynced, syncError };
}
